import type { AxiosInstance } from 'axios';
import { DASHBOARD_CONFIG } from '@/config/dashboard';
import type { ClassTri } from '../../domain/entities/class-tri.entity';
import type { StudentTri } from '../../domain/entities/student-try.entity';
import type { ClassTriRanking } from '../../domain/entities/class-tri-ranking.entity';
import type { ClassTriConfidence } from '../../domain/entities/class-tri-confidence.entity';
import type { ClassTriExcluded } from '../../domain/entities/class-tri-excluded.entity';
import type { ClassTriConfidenceStudent } from '../../domain/entities/class-tri-confidence-student.entity';

export class TriApiDatasource {
  private readonly client: AxiosInstance;

  constructor(client: AxiosInstance) {
    this.client = client;
  }

  async getClassTri(editionId: string, area?: string): Promise<ClassTri> {
    const { data } = await this.client.get<ClassTri>(
      '/v1/mockexamperformancedashboard/tri/class-tri',
      {
        params: { editionId, area, courseId: DASHBOARD_CONFIG.courseId },
      },
    );

    return data;
  }

  async getClassTriRanking(
    editionId: string,
    page: number,
    pageSize: number,
    area?: string,
  ): Promise<ClassTriRanking> {
    const { data } = await this.client.get<ClassTriRanking>(
      '/v1/mockexamperformancedashboard/tri/class-tri-ranking',
      {
        params: {
          editionId,
          area,
          page,
          pageSize,
          courseId: DASHBOARD_CONFIG.courseId,
        },
      },
    );

    return data;
  }

  async getClassTriConfidence(
    editionId: string,
    includeExtra?: boolean,
  ): Promise<ClassTriConfidence> {
    const { data } = await this.client.get<ClassTriConfidence>(
      '/v1/mockexamperformancedashboard/tri/class-tri-confidence',
      {
        params: {
          editionId,
          includeExtra,
          courseId: DASHBOARD_CONFIG.courseId,
        },
      },
    );

    return data;
  }

  async getClassTriConfidenceStudents(
    editionId: string,
    includeExtra?: boolean,
  ): Promise<{ students: ClassTriConfidenceStudent[] }> {
    const { data } = await this.client.get<{
      students: ClassTriConfidenceStudent[];
    }>('/v1/mockexamperformancedashboard/tri/class-tri-confidence-students', {
      params: { editionId, includeExtra, courseId: DASHBOARD_CONFIG.courseId },
    });

    return data;
  }

  async getClassTriExcluded(
    editionId: string,
    page: number,
    pageSize: number,
  ): Promise<ClassTriExcluded> {
    const { data } = await this.client.get<ClassTriExcluded>(
      '/v1/mockexamperformancedashboard/tri/class-tri-excluded',
      {
        params: {
          editionId,
          page,
          pageSize,
          courseId: DASHBOARD_CONFIG.courseId,
        },
      },
    );

    return data;
  }

  async getStudentTri(
    editionId: string,
    email: string,
    area?: string,
  ): Promise<StudentTri> {
    const { data } = await this.client.get<StudentTri>(
      '/v1/mockexamperformancedashboard/tri/student-tri',
      {
        params: {
          editionId,
          email,
          area,
          courseId: DASHBOARD_CONFIG.courseId,
        },
      },
    );

    return data;
  }
}
